import { useEffect, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { API } from "@/App";

export const WEEKDAYS = [
  { d: 1, l: "Pzt" }, { d: 2, l: "Sal" }, { d: 3, l: "Çar" }, { d: 4, l: "Per" },
  { d: 5, l: "Cum" }, { d: 6, l: "Cmt" }, { d: 0, l: "Paz" },
];
const MAX_DATES = 60;

// Dates for a recurring reservation: daily / weekly / selected weekdays, ending by count or end date
export const computeRepeatDates = ({ start, enabled, type, days, endMode, count, until }) => {
  if (!start) return [];
  const first = format(start, "yyyy-MM-dd");
  if (!enabled) return [first];
  const byCount = endMode !== "date";
  if (!byCount && !until) return [];
  const limit = byCount ? Math.max(1, count || 1) : MAX_DATES;
  const out = [];
  for (let i = 0; out.length < limit && i < 366; i++) {
    const d = addDays(start, type === "weekly" ? i * 7 : i);
    const ds = format(d, "yyyy-MM-dd");
    if (!byCount && until && ds > until) break;
    if (type === "weekdays" && !(days || []).includes(d.getDay())) continue;
    out.push(ds);
  }
  return out;
};

export const newSeriesId = () => `ser_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const checkDate = async ({ date, startTime, endTime, trainerId, roomId, branch, trainingType, excludeId, memberId, businessId }) => {
  const q = (base) => {
    let u = `${base}?date=${date}&time=${startTime}&end_time=${endTime}`;
    if (excludeId) u += `&exclude_reservation_id=${encodeURIComponent(excludeId)}`;
    if (trainingType) u += `&training_type=${encodeURIComponent(trainingType)}`;
    if (branch) u += `&branch=${encodeURIComponent(branch)}`;
    return u;
  };
  const reasons = [];
  try {
    if (trainerId) {
      const r = await fetch(q(`${API}/trainers/${trainerId}/availability`) + (roomId ? `&room_id=${encodeURIComponent(roomId)}` : ""), { credentials: "include" });
      const d = r.ok ? await r.json() : null;
      if (d && d.is_available === false) reasons.push(d.reason || "Antrenör müsait değil");
    }
    if (roomId) {
      const r = await fetch(q(`${API}/rooms/${roomId}/availability`), { credentials: "include" });
      const d = r.ok ? await r.json() : null;
      if (d && d.is_available === false) reasons.push(d.lock_reason || "Oda dolu");
    }
    if (memberId && businessId) {
      const r = await fetch(`${API}/reservations/check-conflicts`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ business_id: businessId, member_id: memberId, date, start_time: startTime, end_time: endTime, exclude_reservation_id: excludeId }) });
      const d = r.ok ? await r.json() : null;
      const m = (d?.conflicts || []).find((c) => c.type === "member");
      if (m) reasons.push("Üyenin bu saatte başka rezervasyonu var");
    }
  } catch {
    // network errors are not shown as conflicts
  }
  return reasons;
};

// Preview list: every date that will be created; unavailable ones are red
export const RecurringPreview = ({ dates, slot, maxAllowed, onStatus, minDates = 2 }) => {
  const [checks, setChecks] = useState({});
  const key = JSON.stringify({ dates, slot });
  useEffect(() => {
    let alive = true;
    setChecks({});
    if (!slot.startTime || !slot.endTime || dates.length < minDates) return undefined;
    const timer = setTimeout(() => {
      Promise.all(dates.map(async (date) => [date, await checkDate({ ...slot, date })])).then((rows) => {
        if (alive) setChecks(Object.fromEntries(rows));
      });
    }, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const bad = dates.filter((d) => checks[d]?.length);
  const overLimit = maxAllowed != null && dates.length > maxAllowed;
  const pending = dates.some((d) => checks[d] === undefined);
  useEffect(() => { onStatus?.({ bad: bad.length, overLimit, badDates: bad, pending }); }, [bad.join(), overLimit, pending]); // eslint-disable-line react-hooks/exhaustive-deps
  if (dates.length < minDates) return null;
  return (
    <div className="pt-2 border-t border-purple-200 space-y-1" data-testid="recurring-preview">
      <p className="text-xs font-semibold text-purple-800">
        Oluşacak {dates.length} rezervasyon: {format(parseISO(dates[0]), "d MMM", { locale: tr })} – {format(parseISO(dates[dates.length - 1]), "d MMM yyyy", { locale: tr })}
      </p>
      {bad.length > 0 && (
        <p className="text-xs font-semibold text-red-600" data-testid="recurring-bad-dates">{bad.length} tarih uygun değil; bu tarihler oluşturulmayacak, diğerleri oluşturulacak.</p>
      )}
      {overLimit && (
        <p className="text-xs font-semibold text-red-600" data-testid="recurring-over-limit">Pakette kalan hak {maxAllowed}. En fazla {maxAllowed} ders seçebilirsiniz.</p>
      )}
      <div className="max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
        {dates.map((d, i) => {
          const r = checks[d];
          const over = maxAllowed != null && i >= maxAllowed;
          const isBad = over || r?.length;
          return (
            <div key={d} className={`text-xs px-2 py-1 rounded-md ${isBad ? "bg-red-100 text-red-700" : "bg-white text-purple-900"}`} data-testid={`recurring-date-${d}`}>
              {format(parseISO(d), "d MMM EEE", { locale: tr })}
              {r === undefined && !over ? " · kontrol ediliyor" : ""}
              {over ? " · pakette hak yok" : r?.length ? ` · ${r[0]}` : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const WeekdayPicker = ({ days, fixedDay, onChange }) => (
  <div className="flex flex-wrap gap-1" data-testid="recurring-weekday-picker">
    {WEEKDAYS.map(({ d, l }) => {
      const fixed = d === fixedDay;
      const on = fixed || days.includes(d);
      return (
        <button key={d} type="button" disabled={fixed} title={fixed ? "Rezervasyon tarihinin günü, kaldırılamaz" : undefined}
          onClick={() => onChange(on ? days.filter((x) => x !== d) : [...days, d])}
          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${fixed ? "bg-purple-800 text-white border-purple-800 cursor-not-allowed ring-2 ring-purple-300" : on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-purple-800 border-purple-200 hover:bg-purple-100"}`}
          data-testid={`recurring-day-${d}`}>
          {l}
        </button>
      );
    })}
  </div>
);
